// ** Types
import type { AppContext } from '@/types/contextTypes';
import type { StrapiCompetitionCardItem } from '@/features/ap-plus/types/ap-plus-strapi';
import type { CompetitionCardModel, CompetitionEntryDetail } from '@/components/common/CompetitionCard';

const toStringId = (value: string | number | undefined, index: number) => String(value ?? `competition-card-${index}`);

export const getRankingLabel = (item: CompetitionCardModel): string | null =>
	item.ranking != null ? `Platz ${item.ranking} - ${item.description}` : null;

/**
 * Maps raw Strapi competition card items to the view-model consumed by
 * `<CompetitionCard />` and `<CompetitionDetailOverlay />`.
 *
 * Adjust field mappings once the Strapi schema is finalised.
 */
export const mapCompetitionGrid = (
	items: StrapiCompetitionCardItem[] | undefined,
	host: AppContext['host']
): CompetitionCardModel[] => {
	if (!items?.length) return [];

	return items.map((item, index) => {
		const extraImages =
			item.extraImages?.data
				?.filter((media) => media?.attributes?.url)
				.map((media) => ({
					src: `${host}${media.attributes.url}`,
					alt: media.attributes.alternativeText || undefined
				})) ?? [];

		const hasDetail =
			item.fullDescription || item.trainingCompany || item.guild || item.material || extraImages.length > 0;

		const detail: CompetitionEntryDetail | undefined = hasDetail
			? {
					fullDescription: item.fullDescription || undefined,
					trainingCompany: item.trainingCompany?.trim() || undefined,
					guild: item.guild?.trim() || undefined,
					material: item.material?.trim() || undefined,
					extraImages: extraImages.length > 0 ? extraImages : undefined
				}
			: undefined;

		return {
			id: toStringId(item.id, index),
			name: item.name?.trim() || '',
			description: item.description?.trim() || '',
			ctaLabel: item.ctaLabel?.trim() || 'Mehr erfahren',
			ctaHref: item.ctaHref?.trim() || '#',
			ranking: ([1, 2, 3] as const).includes(item.ranking as 1 | 2 | 3) ? (item.ranking as 1 | 2 | 3) : undefined,
			imageSrc: item.image?.data?.attributes?.url ? `${host}${item.image.data.attributes.url}` : undefined,
			imageAlt: item.image?.data?.attributes?.alternativeText || undefined,
			detail
		};
	});
};
